import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '../../../components/layout/AppShell';
import TechDaySelector, {
    addDays,
    dayKey,
    sameDay,
    startOfDay
} from '../../../components/technician/TechDaySelector';
import TechnicianWorkOrderPanel from '../../../components/work-order/TechnicianWorkOrderPanel';
import '../technician.css';
import { apiRequest, openApiFile } from '../../../api/client';

type JobDoc = {
    name: string;
    url: string;
    type?: string;
    size?: number;
};

type JobReport = {
    _id: string;
    reportDate?: string;
    status?: 'draft' | 'submitted';
    submittedAt?: string | null;
    technicianName?: string;
};

type WorkOrderRow = {
    _id: string;
    title?: string;
    status?: string;
    instructions?: string;
    formTemplateId?: string;
    client?: { name?: string; address?: string; phone?: string; email?: string };
    documents?: JobDoc[];
    schedule?: { date?: string; startTime?: string; endTime?: string };
};

type JobDetail = {
    id: string;
    site: string;
    address: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    task: string;
    notes: string;
    status: string;
    docs: JobDoc[];
};

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function mapJobDetail(job: WorkOrderRow): JobDetail {
    return {
        id: job._id,
        site: job.client?.name || 'Unknown Site',
        address: job.client?.address || '—',
        contactName: job.client?.name || '—',
        contactPhone: job.client?.phone || '—',
        contactEmail: job.client?.email || '—',
        task: job.title || 'Work Order',
        notes: job.instructions || 'No additional notes.',
        status: job.status || 'Scheduled',
        docs: Array.isArray(job.documents) ? job.documents : []
    };
}

function JobLog(): React.JSX.Element {
    const today = useMemo(() => startOfDay(new Date()), []);
    const [selectedDay, setSelectedDay] = useState<Date>(today);
    const [workOrders, setWorkOrders] = useState<WorkOrderRow[]>([]);
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [activeWorkOrder, setActiveWorkOrder] = useState<WorkOrderRow | null>(null);
    const [reports, setReports] = useState<JobReport[]>([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [openingDoc, setOpeningDoc] = useState<string | null>(null);

    useEffect(() => {
        apiRequest<{ data: WorkOrderRow[] }>('/api/work-orders')
            .then((data) => setWorkOrders(data.data || []))
            .catch(() => setWorkOrders([]));
    }, []);

    const jobsByDay = useMemo(() => {
        const map = new Map<string, WorkOrderRow[]>();
        for (const job of workOrders) {
            if (!job.schedule?.date) continue;
            const d = new Date(job.schedule.date);
            if (Number.isNaN(d.getTime())) continue;
            const key = dayKey(startOfDay(d));
            const list = map.get(key) || [];
            list.push(job);
            map.set(key, list);
        }
        map.forEach((list) => {
            list.sort((a, b) => (a.schedule?.startTime || '99:99').localeCompare(b.schedule?.startTime || '99:99'));
        });
        return map;
    }, [workOrders]);

    const counts = useMemo(() => {
        const m = new Map<string, number>();
        jobsByDay.forEach((list, key) => m.set(key, list.length));
        return m;
    }, [jobsByDay]);

    const selectedDayKey = dayKey(selectedDay);
    const selectedJobs = useMemo(
        () => jobsByDay.get(selectedDayKey) || [],
        [jobsByDay, selectedDayKey]
    );
    const jobDetails = useMemo(() => selectedJobs.map(mapJobDetail), [selectedJobs]);

    useEffect(() => {
        if (selectedJobs.length === 0) {
            setActiveJobId(null);
            return;
        }
        setActiveJobId((prev) => {
            if (prev && selectedJobs.some((j) => j._id === prev)) return prev;
            return selectedJobs[0]._id;
        });
    }, [selectedJobs]);

    const activeJob = jobDetails.find((job) => job.id === activeJobId) || jobDetails[0];
    const activeRow = selectedJobs.find((job) => job._id === activeJobId) || selectedJobs[0] || null;
    const hasWorkOrderForm = Boolean(activeWorkOrder?.formTemplateId ?? activeRow?.formTemplateId);

    useEffect(() => {
        if (!activeJobId) {
            setActiveWorkOrder(null);
            return;
        }
        let cancelled = false;
        apiRequest<{ data: WorkOrderRow }>(`/api/work-orders/${activeJobId}`)
            .then((res) => {
                if (!cancelled) setActiveWorkOrder(res.data);
            })
            .catch(() => {
                if (!cancelled) {
                    const fallback = workOrders.find((j) => j._id === activeJobId) || null;
                    setActiveWorkOrder(fallback);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [activeJobId, workOrders]);

    useEffect(() => {
        if (!activeJob?.id || !hasWorkOrderForm) {
            setReports([]);
            return;
        }
        let cancelled = false;
        setReportsLoading(true);
        apiRequest<{ data: JobReport[] }>(`/api/work-orders/${activeJob.id}/reports`)
            .then((res) => {
                if (!cancelled) setReports(res.data || []);
            })
            .catch(() => {
                if (!cancelled) setReports([]);
            })
            .finally(() => {
                if (!cancelled) setReportsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [activeJob?.id, hasWorkOrderForm]);

    const selectedHeading = `${DOW[selectedDay.getDay()]}, ${MONTHS[selectedDay.getMonth()]} ${selectedDay.getDate()}`;
    const relLabel = sameDay(selectedDay, today)
        ? 'Today'
        : sameDay(selectedDay, addDays(today, 1))
          ? 'Tomorrow'
          : sameDay(selectedDay, addDays(today, -1))
            ? 'Yesterday'
            : selectedDay.getTime() < today.getTime()
              ? 'Past'
              : 'Upcoming';

    async function openDocument(doc: JobDoc) {
        const key = doc.url || doc.name;
        if (openingDoc) return;
        setOpeningDoc(key);
        try {
            await openApiFile(doc.url, doc.name || 'document.pdf');
        } catch {
            /* ignore — user can retry */
        } finally {
            setOpeningDoc(null);
        }
    }

    return (
        <AppShell>
            <div className="tech-page">
                <header className="tech-hero">
                    <div>
                        <p className="tech-kicker">Field Notes</p>
                        <h1 className="tech-title">Job Log</h1>
                        <p className="tech-sub">Pick a day and complete the work order form when one is assigned to the job.</p>
                    </div>
                    <div className="tech-actions">
                        <button type="button" className="tech-btn tech-btn-primary" onClick={() => setSelectedDay(today)}>
                            Today
                        </button>
                    </div>
                </header>

                <div className="tech-job-grid">
                    <div className="tech-job-log-sidebar">
                        <TechDaySelector selectedDay={selectedDay} onSelectDay={setSelectedDay} counts={counts} />
                        <div className="tech-card tech-job-list">
                            <h3>{relLabel}&apos;s jobs</h3>
                            <p className="tech-sub" style={{ margin: '0 0 12px' }}>{selectedHeading}</p>
                            <ul className="tech-list">
                                {selectedJobs.length === 0 && (
                                    <li>
                                        <span className="tech-sub">No jobs scheduled for this day.</span>
                                    </li>
                                )}
                                {jobDetails.map((job) => (
                                    <li
                                        key={job.id}
                                        onClick={() => setActiveJobId(job.id)}
                                        className={job.id === activeJobId ? 'tech-job-active' : ''}
                                    >
                                        <div>
                                            <p className="dash-list-title">{job.site}</p>
                                            <p className="dash-list-meta">{job.task}</p>
                                        </div>
                                        <span className="tech-pill">{job.status}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="tech-card tech-job-detail">
                        {!activeJob ? (
                            <div className="tech-empty">
                                <h3>No jobs for this day</h3>
                                <p>Select another day or check Job List for your full schedule.</p>
                            </div>
                        ) : (
                            <>
                                <div className="tech-job-header">
                                    <div>
                                        <h3>{activeJob.site}</h3>
                                        <p className="tech-sub">{activeJob.address}</p>
                                    </div>
                                    <span className="tech-badge ok">{activeJob.status}</span>
                                </div>

                                <div className="tech-detail-grid">
                                    <div>
                                        <p className="tech-label">Primary Contact</p>
                                        <p><strong>{activeJob.contactName}</strong></p>
                                        <p>{activeJob.contactPhone}</p>
                                        <p>{activeJob.contactEmail}</p>
                                    </div>
                                    <div>
                                        <p className="tech-label">Task</p>
                                        <p><strong>{activeJob.task}</strong></p>
                                        <p className="tech-sub">{activeJob.notes}</p>
                                    </div>
                                </div>

                                <div className="tech-docs">
                                    <p className="tech-label">Documents & Blueprints</p>
                                    <div className="tech-doc-list">
                                        {activeJob.docs.length === 0 && <span className="tech-sub">No documents uploaded.</span>}
                                        {activeJob.docs.map((doc) => {
                                            const key = doc.url || doc.name;
                                            const busy = openingDoc === key;
                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className="tech-doc"
                                                    disabled={busy}
                                                    onClick={() => void openDocument(doc)}
                                                >
                                                    {busy ? 'Opening…' : `📄 ${doc.name}`}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {hasWorkOrderForm ? (
                                    <div className="tech-docs">
                                        <p className="tech-label">Previous Reports</p>
                                        <ul className="tech-list">
                                            {reportsLoading && (
                                                <li>
                                                    <span className="tech-sub">Loading reports…</span>
                                                </li>
                                            )}
                                            {!reportsLoading && reports.length === 0 && (
                                                <li>
                                                    <span>No reports available.</span>
                                                    <span className="tech-pill">—</span>
                                                </li>
                                            )}
                                            {!reportsLoading &&
                                                reports.map((report) => (
                                                    <li key={report._id}>
                                                        <span>
                                                            {report.reportDate
                                                                ? new Date(report.reportDate).toLocaleDateString()
                                                                : 'Report'}
                                                            {report.technicianName ? ` · ${report.technicianName}` : ''}
                                                        </span>
                                                        <span className={`tech-pill ${report.status === 'submitted' ? 'ok' : ''}`}>
                                                            {report.status === 'submitted' ? 'Submitted' : 'Draft'}
                                                        </span>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                ) : null}

                                {hasWorkOrderForm ? (
                                    <div className="tech-wo-form-section">
                                        <p className="tech-label">Work order form</p>
                                        <TechnicianWorkOrderPanel
                                            key={`${activeJob.id}-${dayKey(selectedDay)}`}
                                            workOrderId={activeJob.id}
                                            workOrder={activeWorkOrder}
                                            reportDate={selectedDay}
                                            embedded
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}

export default JobLog;
